import { Injectable } from '@angular/core';
import {
    find,
    flattenDepth,
    get,
    has,
    join,
    replace,
    set,
    split,
    values,
} from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Tag } from 'src/app/models/tag';
import { TagGroup } from '../models/tag-group';
import { TagResourceService } from './tag-resource.service';

@Injectable({ providedIn: 'root' })
export class TagService {
    tagCache = {};

    constructor(private tagResource: TagResourceService) {}

    getTags(parent: string, forceRefreshFromServer = false): Observable<Tag[]> {
        const isInCache = has(this.tagCache, parent);
        if (!isInCache || forceRefreshFromServer) {
            return this.tagResource.get(parent).pipe(
                map((tagList) => {
                    if (tagList.length === 0) {
                        tagList = [];
                    }
                    set(this.tagCache, parent, tagList);
                    return tagList;
                })
            );
        } else {
            return of(get(this.tagCache, parent, Tag[0]));
        }
    }

    getGroupedTags(parent: string): Observable<TagGroup[]> {
        const cacheName = `grouped-${parent}`;
        if (has(this.tagCache, cacheName)) {
            return of(get(this.tagCache, cacheName, []));
        }

        // groups have been chosen by UX design and are purely for display,
        // icons within groups are purposely in this order per UX design
        const numbers = Array.from({ length: 24 }, (_, i) => i + 1).map((x) =>
            x.toString()
        );
        const letters = Array.from({ length: 26 }, (_, i) => i + 97).map((x) =>
            String.fromCharCode(x)
        );
        const onRoad = [
            'mark_solid',
            'mark_outline',
            'lodging',
            'airport',
            'automotive',
            'business',
            'bar',
            'community',
            'bank',
            'gas',
            'entertainment',
            'marine',
            'medical',
            'parking',
            'restaurant',
            'shopping',
            'recreation',
            'transportation',
            'attraction',
            'dealership',
            'left_turn',
            'right_turn',
            'straight',
            'u_turn',
            'historic_site',
        ];
        const outdoors = [
            'picnic_area',
            'restrooms',
            'camping',
            'rv_parking',
            'structure',
            'vehicle',
            'drinking_water',
            'trailhead',
            'junction',
            'gate',
            'hazzard',
            'forest',
            'meadow',
            'wash',
            'water',
            'bridge',
            'stream_crossing',
            'beach',
            'scenic',
            'summit',
            'ski',
            'fishing',
            'ice_fishing',
            'swimming',
            'mud',
        ];
        const hunting = [
            'blind',
            'game_trail',
            'stand',
            'feeder',
            'trap',
            'trail_camera',
            'viewpoint',
            'bedding',
            'blood_trail',
            'scat',
            'tracks',
            'rub',
            'shed_antler',
            'food_source',
            'water_source',
            'deer',
            'antelope',
            'elk',
            'bear',
            'boar',
            'small_game',
            'turkey',
            'fowl',
            'shot',
        ];

        return this.getTags(parent).pipe(
            map((tags) => {
                const res = [
                    new TagGroup(
                        $localize`On Road`,
                        onRoad
                            .map((entry) =>
                                tags.find((tag) => tag.tagName === entry)
                            )
                            .filter(Boolean) as Tag[]
                    ),
                    new TagGroup(
                        $localize`Outdoors`,
                        outdoors
                            .map((entry) =>
                                tags.find((tag) => tag.tagName === entry)
                            )
                            .filter(Boolean) as Tag[]
                    ),
                    new TagGroup(
                        $localize`Hunting`,
                        hunting
                            .map((entry) =>
                                tags.find((tag) => tag.tagName === entry)
                            )
                            .filter(Boolean) as Tag[]
                    ),
                    new TagGroup(
                        $localize`Numbers`,
                        numbers
                            .map((entry) =>
                                tags.find((tag) => tag.tagName === entry)
                            )
                            .filter(Boolean) as Tag[]
                    ),
                    new TagGroup(
                        $localize`Letters`,
                        letters
                            .map((entry) =>
                                tags.find((tag) => tag.tagName === entry)
                            )
                            .filter(Boolean) as Tag[]
                    ),
                ];
                set(this.tagCache, cacheName, res);
                return res;
            })
        );
    }

    getAllParentTags(forceRefreshFromServer = false): Observable<Tag[]> {
        return this.getTags('all-parents', forceRefreshFromServer);
    }

    getTagImage(tag: string, parent: string): Observable<string | null> {
        const cachedImage = this.getTagImageFromCache(tag);
        if (cachedImage) {
            return of(this.urlToSdf(cachedImage));
        }

        return this.getTags(parent).pipe(
            map((tags) => {
                const result = find(tags, (t) => t.tagName === tag);
                if (result) {
                    return this.urlToSdf(result.iconAssetUrl);
                }

                return null;
            })
        );
    }

    getTagImageFromCache(tag: string): string | null {
        const allTags: Tag[] = flattenDepth(values(this.tagCache), 1);

        if (allTags.length === 0) {
            return null;
        }

        const foundTag = find(allTags, (t) => t.tagName === tag);

        if (!foundTag) {
            return null;
        }

        return foundTag.iconAssetUrl;
    }

    private urlToSdf(tag: string): string | null {
        if (!tag) {
            return null;
        }

        // Split the tag by slashes
        const parts = split(tag, '/');

        if (parts.length < 2) {
            return tag;
        }

        parts.splice(parts.length - 1, 0, 'sdf');
        const sdfPath = join(parts, '/');
        return replace(sdfPath, '.svg', '.png');
    }
}
